import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../shared/DashboardLayout';
import ClinicalMapper from '../../ClinicalMapper';
import ListsManager from '../../ListsManager';
import UserManagement from '../UserManagement';
import RBACManagement from '../RBACManagement';
import AuditLog from '../AuditLog';
import IPCSetup from '../IPCSetup';
import NomenclatureManager from '../protocols/nomenclatures/NomenclatureManager';
import ProtocolConfiguration from '../protocols/ProtocolConfiguration';
import ProtocolTemplateLibrary from '../protocols/ProtocolTemplateLibrary';
import ProtocolAnalyticsDashboard from '../protocols/ProtocolAnalyticsDashboard';
import RisksList from '../risks/RisksList';
import DeviationList from '../deviations/DeviationList';
import RiskAnalyticsDashboard from '../risks/RiskAnalyticsDashboard';
import LocationRiskManager from '../risk/LocationRiskManager';
import TaskTemplateManager from '../tasks/templates/TaskTemplateManager';
import TaskGenerator from '../tasks/TaskGenerator';
import MyTasksDashboard from '../tasks/MyTasksDashboard';
import TaskMonitoringDashboard from '../tasks/TaskMonitoringDashboard';
import TaskHistoryDashboard from '../tasks/TaskHistoryDashboard';
import TaskNomenclatureManager from '../tasks/nomenclatures/TaskNomenclatureManager';
import UserGroupManager from '../tasks/usergroups/UserGroupManager';
import ExecutionAnalyticsDashboard from '../execution/ExecutionAnalyticsDashboard';
import { APP_LOGO_SRC } from '../../constants/appLogo';
// Phase 4 Components
import PatientList from '../patients/PatientList';
import PatientRiskFactorManager from '../risks/PatientRiskFactorManager';
import EnvironmentalRiskFactorManager from '../risks/EnvironmentalRiskFactorManager';
import EnvironmentalAssessmentManager from '../clinical/EnvironmentalAssessmentManager';
import AlertManagement from '../alerts/AlertManagement';
// Policy Framework & EU Diseases Implementation
import PolicyFrameworkManager from '../admin/PolicyFrameworkManager';
import PolicyDocumentManager from '../admin/PolicyDocumentManager';
import ComplianceReportView from '../admin/ComplianceReportView';
import ProcessRegistry from '../admin/ProcessRegistry';
import ProcessCategoryManager from '../admin/ProcessCategoryManager';
import ProtocolProcessMapping from '../admin/ProtocolProcessMapping';
import EUDiseaseManager from '../compliance/EUDiseaseManager';
import DiagnosisManager from '../admin/DiagnosisManager';
import IncidentList from '../incidents/IncidentList';
import RiskClassificationNomenclature from '../riskClassification/RiskClassificationNomenclature';
import RiskMapView from '../riskClassification/RiskMapView';
import PerformanceMetricManager from '../qms/PerformanceMetricManager';
import AuditRecordManager from '../qms/AuditRecordManager';
import AuditQMSManager from '../qms/AuditQMSManager';
import AuditQMSDetail from '../qms/AuditQMSDetail';
import AuditThemesManager from '../settings/AuditThemesManager';
import AuditVocabularyManager from '../settings/AuditVocabularyManager';
import LetterheadManager from '../settings/LetterheadManager';
import SamplingDashboard from '../sampling/SamplingDashboard';
import SurfaceTypesManager from '../settings/SurfaceTypesManager';
import LabSettingsManager from '../settings/LabSettingsManager';
import ATPSchedulerSettings from '../settings/ATPSchedulerSettings';
import CAPAManager from '../qms/CAPAManager';
import RecommendationRegistry from '../qms/RecommendationRegistry';
import RCAList from '../rca/RCAList';
import CapabilityProfileView from '../michelangelo/CapabilityProfileView';
import IPCAFAssessment from '../michelangelo/IPCAFAssessment';
import SetupWizard from '../setup/SetupWizard';
import HHSAFAssessment from '../who/HHSAFAssessment';
import SurgicalSafetyAssessment from '../who/SurgicalSafetyAssessment';
import PatientSafetyAssessment from '../who/PatientSafetyAssessment';
import SafeChildbirthAssessment from '../who/SafeChildbirthAssessment';
import Checklist753Assessment from '../who/Checklist753Assessment';
import WHOAssessmentResults from '../who/WHOAssessmentResults';
import AssessmentHistoryList, { SOURCE_PROFILE, SOURCE_ASSESSMENT_RESULTS } from '../who/AssessmentHistory';
import AssessmentSummaryView from '../who/AssessmentSummaryView';
import './ManagementDashboard.css';
import { useOrg } from '../../hooks/useOrg';

// Admin/analytics-only: lazy-load to keep @xyflow and Recharts/d3 out of initial bundle
const WorkflowManagement = lazy(() => import('../WorkflowManagement'));
const ExecutiveDashboard = lazy(() => import('../analytics/ExecutiveDashboard'));
const DepartmentAnalytics = lazy(() => import('../analytics/DepartmentAnalytics'));
const ComplianceDashboard = lazy(() => import('../compliance/ComplianceDashboard'));

/**
 * Management Dashboard
 * Full system access and control for management role
 */
const ManagementDashboard = ({ onLogout, userRole, userData, structure, setStructure, dashboardConfig, forceSetupWizard, onOrganizationAttached }) => {
  const { t } = useTranslation(['management', 'common', 'qms', 'rca']);
  const { activeOrgId, organizationIds } = useOrg();
  // Superadmin accounts created by bootstrapPlatform (v4.22.0) with no organization
  // yet land directly on the Setup Wizard instead of the empty dashboard view.
  const [activeSection, setActiveSection] = useState(forceSetupWizard ? 'setup-wizard' : null);
  const [userName, setUserName] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ 'hospital-admin': true });
  const [navigationItems, setNavigationItems] = useState([]);
  const [whoAssessmentResult, setWhoAssessmentResult] = useState(null);
  const [whoAssessmentActiveId, setWhoAssessmentActiveId] = useState(null);
  const [whoAssessmentViewRun, setWhoAssessmentViewRun] = useState(null);
  const [whoAssessmentRunFormActiveId, setWhoAssessmentRunFormActiveId] = useState(null);
  const [whoShowIpcafCapabilityProfile, setWhoShowIpcafCapabilityProfile] = useState(false);
  const [auditDetailId, setAuditDetailId] = useState(null);

  useEffect(() => {
    if (userData) {
      setUserName(userData.full_name || `${userData.first_name} ${userData.last_name}`);
    }
  }, [userData]);

  useEffect(() => {
    // Always use original management navigation for now
    console.log('Management Dashboard - Using original navigation');
    const navigationItems = [
      {
        id: 'hospital-admin',
        label: t('management:hospitalAdministration'),
        type: 'section',
        expanded: expandedSections['hospital-admin'],
        children: [
          { id: 'admin-hospitals', label: t('management:hospitals') },
          { id: 'admin-locations', label: t('management:locations') },
          { id: 'admin-buildings', label: t('management:buildings') },
          { id: 'admin-floors', label: t('management:floors') },
          { id: 'admin-departments', label: t('management:departments') },
          { id: 'admin-rooms', label: t('management:rooms') },
          { id: 'admin-beds', label: t('management:beds') },
          { id: 'admin-devices', label: t('management:devices') },
          { id: 'hospital-letterhead', label: t('management:letterhead') }
        ]
      },
      { type: 'divider' },
      {
        id: 'user-management',
        label: t('management:userManagement'),
        type: 'section',
        expanded: expandedSections['user-management'],
        children: [
          { id: 'user-admin', label: t('management:users') },
          { id: 'user-groups', label: t('management:userGroups') },
          { id: 'rbac', label: t('management:rbacManagement') }
        ]
      },
      {
        id: 'ipc-setup',
        label: t('management:ipcSetup'),
        type: 'section',
        expanded: expandedSections['ipc-setup'],
        children: [
          { id: 'ipc-ic-leadership', label: t('management:icLeadership') },
          { id: 'ipc-risk-management', label: t('management:riskManagement') },
          { id: 'ipc-risk-classification-nomenclature', label: t('management:riskClassificationNomenclature') },
          { id: 'ipc-risk-map', label: t('management:riskMapViewExport') },
          { id: 'ipc-eu-compliance', label: t('management:euCompliance') },
          { id: 'ipc-nomenclatures', label: t('management:nomenclatures') },
          { id: 'ipc-diagnoses', label: t('management:diagnoses') },
          { id: 'ipc-hai-targets', label: t('management:haiTargets') },
          { id: 'ipc-biological-entities', label: t('management:biologicalEntities') },
          { id: 'ipc-biocide', label: t('management:biocideProducts') }
        ]
      },
      {
        id: 'who-assessments',
        label: t('management:whoAssessments'),
        type: 'section',
        expanded: expandedSections['who-assessments'],
        children: [
          { id: 'who-ipcaf', label: t('management:ipcafIpc') },
          { id: 'who-hhsaf', label: t('management:hhsafHandHygiene') },
          { id: 'who-surgical-safety', label: t('management:surgicalSafetyChecklist') },
          { id: 'who-patient-safety', label: t('management:patientSafetyAssessment') },
          { id: 'who-safe-childbirth', label: t('management:safeChildbirthChecklist') },
          { id: 'who-checklist753', label: t('management:auditExtinsIgiena') }
        ]
      },
      {
        id: 'compliance',
        label: t('management:compliance'),
        type: 'section',
        expanded: expandedSections['compliance'],
        children: [
          { id: 'ipc-policy-framework-manager', label: t('management:policyFramework') },
          { id: 'ipc-policy-documents', label: t('management:policyDocuments') },
          { id: 'ipc-compliance-report', label: t('management:complianceReport') },
          { id: 'ipc-process-registry', label: t('management:standardRegistry') },
          { id: 'ipc-protocol-process-mapping', label: t('management:procedureStandardMapping') },
          { id: 'ipc-performance-metrics', label: t('management:performanceMetrics') },
          { id: 'ipc-audit-records', label: t('management:auditRecords') },
          { id: 'ipc-audit-themes', label: t('management:auditThemesNomenclature') },
          { id: 'ipc-audit-vocabulary', label: t('management:auditVocabularyNomenclature') },
          { id: 'ipc-capa', label: t('management:capa') },
          { id: 'ipc-rca-list', label: t('rca:title') },
          { id: 'surface-types', label: 'Tipuri Suprafețe' },
          { id: 'lab-settings', label: 'Configurare Laborator' },
          { id: 'atp-scheduler', label: 'Scheduler ATP' }
        ]
      },
      { id: 'sampling', label: 'Probe & Testare', type: 'item' },
      { id: 'audit-log', label: t('management:auditLog'), type: 'item' },
      { type: 'divider' },
      {
        id: 'patient-risk-management',
        label: t('management:patientRiskManagement'),
        type: 'section',
        expanded: expandedSections['patient-risk-management'],
        children: [
          { id: 'patient-management', label: t('management:patientManagement') },
          { id: 'patient-risk-factors', label: t('management:patientRiskFactors') },
          { id: 'environmental-risk-factors', label: t('management:environmentalRiskFactors') },
          { id: 'environmental-assessment', label: t('management:environmentalAssessment') },
          { id: 'location-risk-profiles', label: t('management:locationRiskProfiles') },
          { id: 'alert-management', label: t('management:alertManagement') }
        ]
      },
      { type: 'divider' },
      { id: 'bpm-workflows', label: t('management:bpmWorkflows'), type: 'item' },
      { type: 'divider' },
      {
        id: 'analytics-reporting',
        label: t('management:analyticsReporting'),
        type: 'section',
        expanded: expandedSections['analytics-reporting'],
        children: [
          { id: 'executive-dashboard', label: t('management:executiveDashboard') },
          { id: 'department-analytics', label: t('management:departmentAnalytics') },
          { id: 'compliance-dashboard', label: t('management:complianceDashboard') },
          { id: 'deviations-registry', label: t('management:deviations') }
        ]
      },
      { type: 'divider' },
      {
        id: 'protocols',
        label: t('management:protocols'),
        type: 'section',
        expanded: expandedSections['protocols'],
        children: [
          { id: 'protocol-configuration', label: t('management:protocolConfiguration') },
          { id: 'protocol-templates', label: t('management:templateLibrary') },
          { id: 'protocol-analytics', label: t('management:protocolAnalytics') },
          { id: 'incident-reports', label: t('management:incidentReports') },
          { id: 'incident-analytics', label: t('management:incidentAnalytics') },
          { id: 'protocol-risks', label: t('management:risks') },
          { id: 'risk-analytics', label: t('management:riskAnalytics') },
          { id: 'protocol-nomenclatures', label: t('management:protocolNomenclatures') }
        ]
      },
      {
        id: 'task-management',
        label: t('management:taskManagement'),
        type: 'section',
        expanded: expandedSections['task-management'],
        children: [
          { id: 'task-templates', label: t('management:taskTemplates') },
          { id: 'task-generation', label: t('management:taskGeneration') },
          { id: 'my-tasks', label: t('management:myTasks') },
          { id: 'task-monitoring', label: t('management:taskMonitoring') },
          { id: 'task-history', label: t('management:taskHistory') },
          { id: 'task-nomenclatures', label: t('management:taskNomenclatures') }
        ]
      },
      { id: 'stocks', label: t('management:stocks'), type: 'item' },
      { type: 'divider' },
      { id: 'setup-wizard', label: t('management:setupWizard'), type: 'item' }
    ];
    setNavigationItems(navigationItems);
  }, [expandedSections]);

  // Navigation items are now generated dynamically based on permissions

  // Active org from OrgContext first; then profile / structure fallbacks (e.g. setup flows)
  const getOrganizationId = () => {
    if (activeOrgId) return activeOrgId;
    // If context has validated org memberships but no active selection yet, use first valid one.
    if (Array.isArray(organizationIds) && organizationIds.length > 0) {
      return organizationIds[0];
    }
    if (structure?.organizations && structure.organizations.length > 0) {
      return structure.organizations[0].id;
    }
    if (structure?.departments && structure.departments.length > 0) {
      return structure.departments[0].organizationId;
    }
    return null;
  };

  // Prefer active org from context (multi-org safe); then legacy profile fields.
  const getCurrentUserOrganizationId = () => {
    if (activeOrgId) return activeOrgId;
    if (Array.isArray(organizationIds) && organizationIds.length > 0) {
      return organizationIds[0];
    }
    return null;
  };

  const handleNavigate = (sectionId) => {
    // Handle section toggle
    if (sectionId && sectionId.startsWith('toggle-')) {
      const section = sectionId.replace('toggle-', '');
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
      return;
    }
    
    // Reset WHO assessment results when navigating away from WHO sections
    if (!sectionId?.startsWith('who-')) {
      setWhoAssessmentResult(null);
      setWhoAssessmentActiveId(null);
    }
    
    setActiveSection(sectionId);
  };

  const renderContent = () => {
    // Hospital Administration sections
    if (activeSection && activeSection.startsWith('admin-')) {
      return (
        <ClinicalMapper 
          structure={structure}
          setStructure={setStructure}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          activeLevel={activeSection}
          goToLists={() => setActiveSection('lists-clinical')}
          userId={userData?.uid}
        />
      );
    }

    switch (activeSection) {
      case 'hospital-letterhead':
        return <LetterheadManager />;

      case 'lists-clinical':
        return <ListsManager structure={structure} setStructure={setStructure} />;
      
      case 'user-admin':
        return <UserManagement departments={structure?.departments || []} />;
      
      case 'rbac':
        return <RBACManagement />;
      
      case 'audit-log':
        return <AuditLog />;
      
      // IPC Setup Sections
      case 'ipc-ic-leadership':
        return <IPCSetup activeSection="ipc-ic-leadership" />;
      
      case 'ipc-risk-management':
        return <IPCSetup activeSection="ipc-risk-management" />;
      
      case 'ipc-risk-classification-nomenclature':
        return (
          <RiskClassificationNomenclature
            organizationId={getCurrentUserOrganizationId()}
            userId={userData?.uid}
          />
        );

      case 'ipc-risk-map':
        return (
          <RiskMapView
            organizationId={getCurrentUserOrganizationId()}
            structure={structure}
          />
        );
      
      case 'who-ipcaf':
        if (whoShowIpcafCapabilityProfile) {
          return (
            <div style={{ padding: '2rem' }}>
              <button type="button" className="btn btn-secondary" style={{ marginBottom: '1rem' }} onClick={() => setWhoShowIpcafCapabilityProfile(false)}>← {t('common:back')}</button>
              <CapabilityProfileView organizationId={getOrganizationId()} userId={userData?.uid} userName={userName} />
            </div>
          );
        }
        if (whoAssessmentViewRun && whoAssessmentActiveId === 'who-ipcaf') {
          return (
            <AssessmentSummaryView
              run={whoAssessmentViewRun}
              instrumentType="scored"
              instrumentName={whoAssessmentViewRun.instrumentName}
              onBack={() => { setWhoAssessmentViewRun(null); }}
            />
          );
        }
        if (whoAssessmentRunFormActiveId === 'who-ipcaf') {
          return (
            <div style={{ padding: '2rem' }}>
              <button type="button" className="btn btn-secondary" style={{ marginBottom: '1rem' }} onClick={() => setWhoAssessmentRunFormActiveId(null)}>← {t('common:back')}</button>
              <IPCAFAssessment
                organizationId={getOrganizationId()}
                userId={userData?.uid}
                userName={userName}
                onComplete={async () => {
                  setWhoAssessmentRunFormActiveId(null);
                  const hospitalCapabilityProfileService = (await import('../../services/hospitalCapabilityProfileService')).default;
                  const runs = await hospitalCapabilityProfileService.getAssessmentHistory(getOrganizationId(), 'IPCAF_2019');
                  if (runs.length > 0) {
                    setWhoAssessmentViewRun(runs[0]);
                    setWhoAssessmentActiveId('who-ipcaf');
                  }
                }}
              />
            </div>
          );
        }
        return (
          <AssessmentHistoryList
            organizationId={getOrganizationId()}
            instrumentId="IPCAF_2019"
            instrumentLabel={t('management:ipcafIpc')}
            source={SOURCE_PROFILE}
            onRunNew={() => setWhoAssessmentRunFormActiveId('who-ipcaf')}
            onViewRun={(run) => { setWhoAssessmentViewRun(run); setWhoAssessmentActiveId('who-ipcaf'); }}
            onViewCapabilityProfile={() => setWhoShowIpcafCapabilityProfile(true)}
          />
        );

      case 'who-hhsaf':
        if (whoAssessmentViewRun && whoAssessmentActiveId === 'who-hhsaf') {
          return (
            <AssessmentSummaryView
              run={whoAssessmentViewRun}
              instrumentType="scored"
              instrumentName={whoAssessmentViewRun.instrumentName}
              onBack={() => { setWhoAssessmentViewRun(null); }}
            />
          );
        }
        if (whoAssessmentRunFormActiveId === 'who-hhsaf') {
          return (
            <div style={{ padding: '2rem' }}>
              <button type="button" className="btn btn-secondary" style={{ marginBottom: '1rem' }} onClick={() => setWhoAssessmentRunFormActiveId(null)}>← {t('common:back')}</button>
              <HHSAFAssessment
                organizationId={getOrganizationId()}
                userId={userData?.uid}
                userName={userName}
                onComplete={(result) => {
                  setWhoAssessmentResult(result);
                  setWhoAssessmentActiveId('who-hhsaf');
                  setWhoAssessmentRunFormActiveId(null);
                }}
              />
            </div>
          );
        }
        if (whoAssessmentResult && whoAssessmentActiveId === 'who-hhsaf') {
          return (
            <WHOAssessmentResults
              result={whoAssessmentResult}
              instrumentType="scored"
              onRedo={() => { setWhoAssessmentResult(null); setWhoAssessmentActiveId(null); }}
              onBackToList={() => { setWhoAssessmentResult(null); setWhoAssessmentActiveId(null); }}
            />
          );
        }
        return (
          <AssessmentHistoryList
            organizationId={getOrganizationId()}
            instrumentId="WHO_HHSAF_2010"
            instrumentLabel={t('management:hhsafHandHygiene')}
            source={SOURCE_PROFILE}
            onRunNew={() => setWhoAssessmentRunFormActiveId('who-hhsaf')}
            onViewRun={(run) => { setWhoAssessmentViewRun(run); setWhoAssessmentActiveId('who-hhsaf'); }}
          />
        );
      
      case 'who-surgical-safety':
        if (whoAssessmentViewRun && whoAssessmentActiveId === 'who-surgical-safety') {
          return (
            <AssessmentSummaryView
              run={whoAssessmentViewRun}
              instrumentType="checklist"
              instrumentName={whoAssessmentViewRun.instrumentName}
              onBack={() => { setWhoAssessmentViewRun(null); }}
            />
          );
        }
        if (whoAssessmentRunFormActiveId === 'who-surgical-safety') {
          return (
            <div style={{ padding: '2rem' }}>
              <button type="button" className="btn btn-secondary" style={{ marginBottom: '1rem' }} onClick={() => setWhoAssessmentRunFormActiveId(null)}>← {t('common:back')}</button>
              <SurgicalSafetyAssessment
                organizationId={getOrganizationId()}
                userId={userData?.uid}
                userName={userName}
                onComplete={(result) => {
                  setWhoAssessmentResult(result);
                  setWhoAssessmentActiveId('who-surgical-safety');
                  setWhoAssessmentRunFormActiveId(null);
                }}
              />
            </div>
          );
        }
        if (whoAssessmentResult && whoAssessmentActiveId === 'who-surgical-safety') {
          return (
            <WHOAssessmentResults
              result={whoAssessmentResult}
              instrumentType="checklist"
              onRedo={() => { setWhoAssessmentResult(null); setWhoAssessmentActiveId(null); }}
              onBackToList={() => { setWhoAssessmentResult(null); setWhoAssessmentActiveId(null); }}
              onViewProfile={() => setActiveSection('who-ipcaf')}
            />
          );
        }
        return (
          <AssessmentHistoryList
            organizationId={getOrganizationId()}
            instrumentId="WHO_SSC_2009"
            instrumentLabel={t('management:surgicalSafetyChecklist')}
            source={SOURCE_PROFILE}
            onRunNew={() => setWhoAssessmentRunFormActiveId('who-surgical-safety')}
            onViewRun={(run) => { setWhoAssessmentViewRun(run); setWhoAssessmentActiveId('who-surgical-safety'); }}
          />
        );
      
      case 'who-patient-safety':
        if (whoAssessmentViewRun && whoAssessmentActiveId === 'who-patient-safety') {
          return (
            <AssessmentSummaryView
              run={whoAssessmentViewRun}
              instrumentType="scored"
              instrumentName={whoAssessmentViewRun.instrumentName}
              onBack={() => { setWhoAssessmentViewRun(null); }}
            />
          );
        }
        if (whoAssessmentRunFormActiveId === 'who-patient-safety') {
          return (
            <div style={{ padding: '2rem' }}>
              <button type="button" className="btn btn-secondary" style={{ marginBottom: '1rem' }} onClick={() => setWhoAssessmentRunFormActiveId(null)}>← {t('common:back')}</button>
              <PatientSafetyAssessment
                organizationId={getOrganizationId()}
                userId={userData?.uid}
                userName={userName}
                onComplete={(result) => {
                  setWhoAssessmentResult(result);
                  setWhoAssessmentActiveId('who-patient-safety');
                  setWhoAssessmentRunFormActiveId(null);
                }}
              />
            </div>
          );
        }
        if (whoAssessmentResult && whoAssessmentActiveId === 'who-patient-safety') {
          return (
            <WHOAssessmentResults
              result={whoAssessmentResult}
              instrumentType="scored"
              onRedo={() => { setWhoAssessmentResult(null); setWhoAssessmentActiveId(null); }}
              onBackToList={() => { setWhoAssessmentResult(null); setWhoAssessmentActiveId(null); }}
            />
          );
        }
        return (
          <AssessmentHistoryList
            organizationId={getOrganizationId()}
            instrumentId="WHO_PSA_2008"
            instrumentLabel={t('management:patientSafetyAssessment')}
            source={SOURCE_PROFILE}
            onRunNew={() => setWhoAssessmentRunFormActiveId('who-patient-safety')}
            onViewRun={(run) => { setWhoAssessmentViewRun(run); setWhoAssessmentActiveId('who-patient-safety'); }}
          />
        );
      
      case 'who-safe-childbirth':
        if (whoAssessmentViewRun && whoAssessmentActiveId === 'who-safe-childbirth') {
          return (
            <AssessmentSummaryView
              run={whoAssessmentViewRun}
              instrumentType="checklist"
              instrumentName={whoAssessmentViewRun.instrumentName}
              onBack={() => { setWhoAssessmentViewRun(null); }}
            />
          );
        }
        if (whoAssessmentRunFormActiveId === 'who-safe-childbirth') {
          return (
            <div style={{ padding: '2rem' }}>
              <button type="button" className="btn btn-secondary" style={{ marginBottom: '1rem' }} onClick={() => setWhoAssessmentRunFormActiveId(null)}>← {t('common:back')}</button>
              <SafeChildbirthAssessment
                organizationId={getOrganizationId()}
                userId={userData?.uid}
                userName={userName}
                onComplete={(result) => {
                  setWhoAssessmentResult(result);
                  setWhoAssessmentActiveId('who-safe-childbirth');
                  setWhoAssessmentRunFormActiveId(null);
                }}
              />
            </div>
          );
        }
        if (whoAssessmentResult && whoAssessmentActiveId === 'who-safe-childbirth') {
          return (
            <WHOAssessmentResults
              result={whoAssessmentResult}
              instrumentType="checklist"
              onRedo={() => { setWhoAssessmentResult(null); setWhoAssessmentActiveId(null); }}
              onBackToList={() => { setWhoAssessmentResult(null); setWhoAssessmentActiveId(null); }}
              onViewProfile={() => setActiveSection('who-ipcaf')}
            />
          );
        }
        return (
          <AssessmentHistoryList
            organizationId={getOrganizationId()}
            instrumentId="WHO_SCC_2015"
            instrumentLabel={t('management:safeChildbirthChecklist')}
            source={SOURCE_PROFILE}
            onRunNew={() => setWhoAssessmentRunFormActiveId('who-safe-childbirth')}
            onViewRun={(run) => { setWhoAssessmentViewRun(run); setWhoAssessmentActiveId('who-safe-childbirth'); }}
          />
        );
      
      case 'who-checklist753':
        if (whoAssessmentViewRun && whoAssessmentActiveId === 'who-checklist753') {
          return (
            <AssessmentSummaryView
              run={whoAssessmentViewRun}
              instrumentType="checklist753"
              instrumentName={whoAssessmentViewRun.instrumentName || t('management:auditExtinsIgiena')}
              onBack={() => { setWhoAssessmentViewRun(null); }}
            />
          );
        }
        if (whoAssessmentRunFormActiveId === 'who-checklist753') {
          return (
            <div style={{ padding: '2rem' }}>
              <button type="button" className="btn btn-secondary" style={{ marginBottom: '1rem' }} onClick={() => setWhoAssessmentRunFormActiveId(null)}>← {t('common:back')}</button>
              <Checklist753Assessment
                organizationId={getOrganizationId()}
                userId={userData?.uid}
                userName={userName}
                onComplete={(result) => {
                  setWhoAssessmentResult(result);
                  setWhoAssessmentActiveId('who-checklist753');
                  setWhoAssessmentRunFormActiveId(null);
                }}
              />
            </div>
          );
        }
        if (whoAssessmentResult && whoAssessmentActiveId === 'who-checklist753') {
          return (
            <WHOAssessmentResults
              result={whoAssessmentResult}
              instrumentType="checklist753"
              onRedo={() => { setWhoAssessmentResult(null); setWhoAssessmentActiveId(null); }}
              onBackToList={() => { setWhoAssessmentResult(null); setWhoAssessmentActiveId(null); }}
            />
          );
        }
        return (
          <AssessmentHistoryList
            organizationId={getOrganizationId()}
            instrumentId="CHECKLIST_753_WHO_HAND_HYGIENE"
            instrumentLabel={t('management:auditExtinsIgiena')}
            source={SOURCE_ASSESSMENT_RESULTS}
            onRunNew={() => setWhoAssessmentRunFormActiveId('who-checklist753')}
            onViewRun={(run) => { setWhoAssessmentViewRun(run); setWhoAssessmentActiveId('who-checklist753'); }}
          />
        );
      
      case 'ipc-policy-framework-manager':
        return <PolicyFrameworkManager 
          organizationId={getOrganizationId()} 
          userId={userData?.uid} 
          userName={userName} 
        />;
      
      case 'ipc-policy-documents':
        return <PolicyDocumentManager 
          organizationId={getOrganizationId()} 
          userId={userData?.uid} 
          userName={userName} 
        />;
      
      case 'ipc-compliance-report':
        return <ComplianceReportView organizationId={getOrganizationId()} />;
      
      case 'ipc-process-registry':
        return <ProcessRegistry 
          organizationId={getOrganizationId()} 
          userId={userData?.uid} 
          userName={userName} 
        />;
      
      case 'ipc-protocol-process-mapping':
        return <ProtocolProcessMapping 
          organizationId={getOrganizationId()} 
          userId={userData?.uid} 
          userName={userName} 
        />;
      
      case 'ipc-eu-compliance':
        return <EUDiseaseManager 
          organizationId={getOrganizationId()} 
          userId={userData?.uid} 
          userName={userName} 
        />;
      
      case 'ipc-nomenclatures':
        return <ProcessCategoryManager />;
      
      case 'ipc-diagnoses':
        return <DiagnosisManager 
          organizationId={getOrganizationId()} 
          userId={userData?.uid} 
          userName={userName} 
        />;
      
      case 'ipc-hai-targets':
        return <IPCSetup activeSection="ipc-hai-targets" />;
      
      case 'ipc-biological-entities':
        return <IPCSetup activeSection="ipc-biological-entities" />;

      case 'ipc-biocide':
        return <IPCSetup activeSection="ipc-biocide" />;
      
      case 'ipc-performance-metrics':
        return <PerformanceMetricManager organizationId={getOrganizationId()} userId={userData?.uid} />;
      
      case 'ipc-audit-records':
        if (auditDetailId) {
          return <AuditQMSDetail 
            userId={userData?.uid} 
            userName={userName}
            auditId={auditDetailId}
            onBack={() => setAuditDetailId(null)}
            onAuditCreated={(id) => setAuditDetailId(id)}
          />;
        }
        return <AuditQMSManager 
          userId={userData?.uid}
          onNavigateToDetail={(id) => setAuditDetailId(id)}
        />;

      case 'ipc-audit-themes':
        return <AuditThemesManager />;

      case 'ipc-audit-vocabulary':
        return <AuditVocabularyManager />;
      
      case 'ipc-capa':
        return (
          <CAPAManager
            organizationId={getOrganizationId()}
            userId={userData?.uid}
            userRole={userRole}
          />
        );

      case 'ipc-recommendations':
        return (
          <RecommendationRegistry
            organizationId={getOrganizationId()}
            userId={userData?.uid}
            userRole={userRole}
          />
        );

      case 'ipc-rca-list':
        return (
          <RCAList
            organizationId={getOrganizationId()}
            userId={userData?.uid}
            userRole={userRole}
          />
        );

      case 'surface-types':
        return <SurfaceTypesManager />;

      case 'lab-settings':
        return <LabSettingsManager />;

      case 'atp-scheduler':
        return <ATPSchedulerSettings />;

      case 'sampling':
        return (
          <SamplingDashboard
            userData={userData}
            canCreate={userData?.role_id !== 'department_head'}
          />
        );
      
      // Protocol Management Sections
      case 'protocol-configuration':
        return <ProtocolConfiguration organizationId={getOrganizationId()} />;
      
      case 'protocol-templates':
        return <ProtocolTemplateLibrary organizationId={getOrganizationId()} onSelectTemplate={(templateData) => {
          // Navigate to protocol configuration with template data
          setActiveSection('protocol-configuration');
          // TODO: Pass template data to ProtocolConfiguration for cloning
        }} />;
      
      case 'protocol-analytics':
        return <ProtocolAnalyticsDashboard />;
      
      case 'incident-reports':
        return (
          <IncidentList
            organizationId={getOrganizationId()}
            userId={userData?.uid}
            userRole={userRole}
            initialView="list"
          />
        );
      
      case 'incident-analytics':
        return (
          <IncidentList
            organizationId={getOrganizationId()}
            userId={userData?.uid}
            userRole={userRole}
            initialView="analytics"
          />
        );
      
      case 'protocol-risks':
        return (
          <RisksList
            organizationId={getOrganizationId()}
            userId={userData?.uid}
            userRole={userRole}
          />
        );
      
      case 'risk-analytics':
        return <RiskAnalyticsDashboard />;
      
      case 'protocol-nomenclatures':
        return <NomenclatureManager />;
      
      // Task Management Sections
      case 'task-templates':
        return <TaskTemplateManager userId={userData?.uid} />;
      
      case 'task-generation':
        return <TaskGenerator userId={userData?.uid} />;
      
      case 'my-tasks':
        return <MyTasksDashboard userId={userData?.uid} />;
      
      case 'task-monitoring':
        return <TaskMonitoringDashboard userId={userData?.uid} />;
      
      case 'task-history':
        return <TaskHistoryDashboard userId={userData?.uid} />;
      
      case 'execution-analytics':
        return <ExecutionAnalyticsDashboard userId={userData?.uid} />;
      
      case 'task-nomenclatures':
        return <TaskNomenclatureManager userId={userData?.uid} />;
      
      case 'user-groups':
        return <UserGroupManager userId={userData?.uid} />;
      
      // Phase 4: Patient & Risk Management Sections
      case 'patient-management':
        return <PatientList />;
      
      case 'patient-risk-factors':
        return <PatientRiskFactorManager />;
      
      case 'environmental-risk-factors':
        return <EnvironmentalRiskFactorManager />;
      
      case 'environmental-assessment':
        return <EnvironmentalAssessmentManager />;
      
      case 'location-risk-profiles':
        return <LocationRiskManager />;
      
      case 'alert-management':
        return <AlertManagement userId={userData?.uid} userName={userName} />;
      
      // BPM Workflow Management
      case 'bpm-workflows':
        return (
          <Suspense fallback={<div className="loading-container"><div className="loading-spinner" /><p>Loading…</p></div>}>
            <WorkflowManagement activeTab="definitions" userRole={userRole} userData={userData} />
          </Suspense>
        );
      // Phase 6: Analytics & Reporting (lazy: Recharts/d3)
      case 'executive-dashboard':
        return (
          <Suspense fallback={<div className="loading-container"><div className="loading-spinner" /><p>Loading…</p></div>}>
            <ExecutiveDashboard />
          </Suspense>
        );
      case 'department-analytics':
        return (
          <Suspense fallback={<div className="loading-container"><div className="loading-spinner" /><p>Loading…</p></div>}>
            <DepartmentAnalytics />
          </Suspense>
        );
      case 'compliance-dashboard':
        return (
          <Suspense fallback={<div className="loading-container"><div className="loading-spinner" /><p>Loading…</p></div>}>
            <ComplianceDashboard organizationId={getCurrentUserOrganizationId()} userData={userData} />
          </Suspense>
        );

      case 'deviations-registry':
        return (
          <DeviationList
            organizationId={getOrganizationId()}
            userId={userData?.uid}
            userRole={userRole}
          />
        );

      case 'stocks':
        return (
          <div className="placeholder-view">
            <div className="placeholder-icon">🚧</div>
            <h2>Coming Soon</h2>
            <p>This section will be implemented based on upcoming user stories.</p>
          </div>
        );
      
      case 'setup-wizard':
        return (
          <SetupWizard
            userData={userData}
            userName={userName}
            structure={structure}
            setStructure={setStructure}
            onComplete={() => setActiveSection(null)}
            onOrganizationAttached={onOrganizationAttached}
          />
        );
      
      default:
        if (activeSection === null) {
          return (
            <div className="empty-dashboard">
              <div className="empty-dashboard-content">
                <img src={APP_LOGO_SRC} alt="ZeroPath" className="empty-dashboard-logo" />
                <h2 className="empty-dashboard-title">Welcome to ZeroPath</h2>
                <p className="empty-dashboard-subtitle">Select a menu item to get started</p>
              </div>
            </div>
          );
        }
        
        return (
          <ClinicalMapper 
            structure={structure}
            setStructure={setStructure}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            activeLevel={activeSection}
            goToLists={() => setActiveSection('lists-clinical')}
            userId={userData?.uid}
          />
        );
    }
  };

  return (
    <DashboardLayout
      userRole={userRole}
      userName={userName}
      userId={userData?.uid}
      onLogout={onLogout}
      navigationItems={navigationItems}
      activeSection={activeSection}
      onNavigate={handleNavigate}
      showNotifications={true}
    >
      {renderContent()}
    </DashboardLayout>
  );
};

export default ManagementDashboard;

